import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Calendar, Video, Mail, Clock, FileText, CheckCircle2, ExternalLink, Briefcase, Award, Target, Star, Shield, Users, X } from 'lucide-react';
import { ContactForm } from './ContactForm';

interface ConsultationBookingProps {
  onClose: () => void;
}

export const ConsultationBooking: React.FC<ConsultationBookingProps> = ({ onClose }) => {
  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 overflow-y-auto">
      <div className="min-h-full flex items-center justify-center p-4">
        <div className="max-w-6xl w-full my-8 relative">
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={onClose}
            className="absolute -top-4 -right-4 p-2 bg-slate-800 text-slate-200 rounded-full hover:bg-slate-700 transition-colors z-50"
          >
            <X className="w-5 h-5" />
          </motion.button>

          <div className="glass-card p-6 md:p-8">
            <div className="flex flex-col md:flex-row items-start md:items-center gap-4 mb-6 pb-6 border-b border-emerald-500/20">
              <div className="p-3 md:p-4 rounded-2xl bg-emerald-500/10">
                <Calendar className="w-6 md:w-8 h-6 md:h-8 text-emerald-500" />
              </div>
              <div>
                <h2 className="text-2xl md:text-3xl font-bold mb-1">Book a Consultation</h2>
                <p className="text-base md:text-lg text-slate-400">Strategic Financial & Business Growth Session with Andrew Young</p>
              </div>
            </div>

            <div className="grid lg:grid-cols-2 gap-8">
              <div className="space-y-6">
                {/* About Section */}
                <div className="glass-card p-6 border border-emerald-500/20 rounded-xl">
                  <div className="flex flex-col md:flex-row items-start gap-6 mb-6">
                    <img
                      src="https://i.ibb.co/KcjPJ7r/profile.jpg"
                      alt="Andrew Young"
                      className="w-32 h-32 rounded-xl object-cover shadow-lg"
                    />
                    <div>
                      <h3 className="text-2xl font-bold text-emerald-500 mb-2">Andrew Young</h3>
                      <p className="text-lg text-slate-300 mb-4">Financial Strategist & Business Growth Expert</p>
                      <div className="flex flex-wrap gap-3">
                        <a
                          href="https://www.linkedin.com/in/andrew-young-b49262243"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="px-4 py-2 bg-emerald-500/10 text-emerald-500 rounded-lg hover:bg-emerald-500/20 transition-colors flex items-center gap-2"
                        >
                          LinkedIn <ExternalLink className="w-4 h-4" />
                        </a>
                        <a
                          href="https://www.unwindaug.com"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="px-4 py-2 bg-emerald-500/10 text-emerald-500 rounded-lg hover:bg-emerald-500/20 transition-colors flex items-center gap-2"
                        >
                          Website <ExternalLink className="w-4 h-4" />
                        </a>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-4">
                    <div className="glass-card p-4 border border-emerald-500/10 rounded-lg">
                      <div className="flex items-start gap-3">
                        <Briefcase className="w-5 h-5 text-emerald-500 mt-1 flex-shrink-0" />
                        <p className="text-slate-300">
                          Founder of multiple successful ventures including a thriving event venue and eCommerce brands exceeding $4M in revenue. I combine real-world business experience with strategic financial planning to help clients achieve both personal and business growth.
                        </p>
                      </div>
                    </div>
                    <div className="glass-card p-4 border border-emerald-500/10 rounded-lg">
                      <div className="flex items-start gap-3">
                        <Target className="w-5 h-5 text-emerald-500 mt-1 flex-shrink-0" />
                        <p className="text-slate-300">
                          Specializing in comprehensive wealth-building strategies that integrate tax optimization, retirement planning, and investment portfolio management. Whether scaling your business or building personal wealth, I provide actionable strategies based on proven success.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Meeting Details */}
                <div>
                  <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
                    <Shield className="w-5 h-5 text-emerald-500" />
                    Meeting Details
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="glass-card p-4 border border-emerald-500/20 rounded-lg">
                      <div className="flex items-center gap-3">
                        <Clock className="w-5 h-5 text-emerald-500" />
                        <div>
                          <p className="font-medium text-slate-200">Duration</p>
                          <p className="text-sm text-slate-400">30 minutes</p>
                        </div>
                      </div>
                    </div>
                    <div className="glass-card p-4 border border-emerald-500/20 rounded-lg">
                      <div className="flex items-center gap-3">
                        <Video className="w-5 h-5 text-emerald-500" />
                        <div>
                          <p className="font-medium text-slate-200">Format</p>
                          <p className="text-sm text-slate-400">Zoom Video Call</p>
                        </div>
                      </div>
                    </div>
                    <div className="glass-card p-4 border border-emerald-500/20 rounded-lg">
                      <div className="flex items-center gap-3">
                        <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                        <div>
                          <p className="font-medium text-slate-200">Cost</p>
                          <p className="text-sm text-slate-400">Free Consultation</p>
                        </div>
                      </div>
                    </div>
                    <div className="glass-card p-4 border border-emerald-500/20 rounded-lg">
                      <div className="flex items-center gap-3">
                        <Mail className="w-5 h-5 text-emerald-500" />
                        <div>
                          <p className="font-medium text-slate-200">Contact</p>
                          <a 
                            href="mailto:andrewyoungfinancial@gmail.com"
                            className="text-sm text-slate-400 hover:text-emerald-500 transition-colors"
                          >
                            Email Me
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Expertise */}
                <div>
                  <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
                    <Star className="w-5 h-5 text-emerald-500" />
                    Areas of Expertise
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {[
                      'Business Growth Strategy',
                      'Wealth Building',
                      'Investment Planning',
                      'Tax Optimization',
                      'Retirement Planning',
                      'Digital Marketing'
                    ].map((area) => (
                      <div key={area} className="glass-card p-4 border border-emerald-500/20 rounded-lg flex items-center gap-3">
                        <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                        <p className="text-slate-300">{area}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Booking Section */}
              <div>
                <div className="glass-card p-8 border border-emerald-500/20 rounded-xl lg:sticky lg:top-6">
                  <div className="space-y-6">
                    <div>
                      <h3 className="text-2xl font-bold mb-3">Schedule Your Session</h3>
                      <p className="text-slate-300 leading-relaxed">
                        Let's discuss your financial goals and business objectives. Whether you're looking to scale your business, optimize your investments, or create a comprehensive wealth-building strategy, I'm here to help.
                      </p>
                    </div>

                    <div className="space-y-4">
                      <h4 className="text-lg font-semibold text-emerald-500 flex items-center gap-2">
                        <Users className="w-5 h-5" />
                        What to Prepare
                      </h4>
                      <div className="space-y-3">
                        {[
                          'Current financial goals and challenges',
                          'Business metrics or investment portfolio details',
                          'Specific areas you want to improve',
                          'Questions about wealth-building strategies'
                        ].map((item) => (
                          <div key={item} className="glass-card p-4 border border-emerald-500/10 rounded-lg flex items-start gap-3">
                            <FileText className="w-5 h-5 text-emerald-500 mt-1" />
                            <p className="text-slate-300">{item}</p>
                          </div>
                        ))}
                      </div>
                    </div>

                    <motion.a
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      href="https://calendly.com/andrewyoungfinancial/30min"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center gap-2 w-full px-8 py-4 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 transition-colors font-medium text-lg"
                    >
                      Book Your Free Consultation
                      <ExternalLink className="w-5 h-5" />
                    </motion.a>
                  </div>
                </div>

                <div className="mt-8">
                  <ContactForm />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};